import { format, formatISO } from "date-fns";

export default function useDate(): {
  getDate: (dateString: string) => Date | null;
  getTime: (dateString: string) => string | null;
  getDateTime: (dateString: string) => Date;
  formatISODate: (date: Date) => string;
  formatISODatetime: (date: Date) => string;
  formatDate: (date: Date | string) => string;
  formatTime: (date: Date | string) => string;
  formatDatetime: (date: Date | string) => string;
  formatDatetimeForFilename: (date: Date | string) => string;
  formatFullDatetime: (date: Date | string) => string;
  formatFullDate: (date: Date | string) => string;
  getNewDate: (date: Date, dayOffset: number) => Date;
  getDayOffset: (eventDate: string, stopTime: string) => number;
  getTimeOffset: (newTime: string, oldTime: string) => number;
  formatStopDate: (date: string, existingDate: string) => string;
  today: Date;
} {
  const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds

  const getDate = (dateString: string) => {
    if (dateString === null || dateString === undefined || dateString === "") return null;
    const dateTimeParts = dateString.split("T");
    const dateParts = dateTimeParts[0].split("-");

    // Create a new Date object with the extracted parts
    return new Date(
      parseInt(dateParts[0], 10), // Year
      parseInt(dateParts[1], 10) - 1, // Month (subtract 1 as months are zero-based)
      parseInt(dateParts[2], 10) // Day
    );
  };

  const getTime = (dateString: string) => {
    if (dateString === null || dateString === undefined || dateString === "") return null;
    const dateTimeParts = dateString.split("T");
    const timeParts = dateTimeParts[1].split(":");
    return `${timeParts[0]}:${timeParts[1]}`;
  };

  const getToday = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  };

  const getDateTime = (dateString: string) => new Date(dateString);

  const formatISODate = (date: Date) => formatISO(date, { representation: "date" });

  const formatISODatetime = (date: Date) => formatISO(date);

  // New function to format datetime for filename
  const formatDatetimeForFilename = (date: Date | string) => {
    const result = !date
      ? ""
      : typeof date === "string" || date instanceof String
      ? format(new Date(date as string), "yyyyMMdd_HHmmss")
      : format(date, "yyyyMMdd_HHmmss");
    return result;
  };

  const formatDate = (date: Date | string) => {
    const result = !date
      ? ""
      : typeof date === "string" || date instanceof String
      ? format(new Date(date as string), "MM/dd/yyyy")
      : format(date, "MM/dd/yyyy");
    return result;
  };

  const formatTime = (date: Date | string) => {
    if (!date) return "";

    if (typeof date === "string") {
      // Check if it's just a time string (e.g., "14:30" or "02:30 PM")
      if (!date.includes("T") && !date.includes("-")) {
        // If it's 24h format (14:30), convert to Date object for formatting
        if (!date.toLowerCase().includes("am") && !date.toLowerCase().includes("pm")) {
          const [hours, minutes] = date.split(":");
          const tempDate = new Date();
          tempDate.setHours(parseInt(hours, 10), parseInt(minutes, 10));
          return format(tempDate, "hh:mm aaa");
        }
        // If it's already in 12h format (02:30 PM), just return it
        return date.toUpperCase();
      }
      // Handle full datetime string
      return format(new Date(date), "hh:mm aaa");
    }

    // Handle Date object
    return format(date, "hh:mm aaa");
  };

  const formatDatetime = (date: Date | string) => {
    return `${formatDate(date)} ${formatTime(date)}`;
  };

  const getNewDate = (date: Date, dayOffset: number) => {
    const newDate = new Date(date.getTime() + dayOffset * oneDay);
    const tzOffset = date.getTimezoneOffset();
    const newTZOffset = newDate.getTimezoneOffset();
    if (tzOffset !== newTZOffset) {
      newDate.setTime(newDate.getTime() + (newTZOffset - tzOffset) * 60 * 1000);
    }
    return newDate;
  };

  const getDayOffset = (newDate: string | Date, oldDate: string | Date) => {
    const newDay = typeof newDate === "string" ? getDate(newDate) as Date : newDate;
    const oldDay = typeof oldDate === "string" ? getDate(oldDate) as Date : oldDate;
    return Math.round((newDay.getTime() - oldDay.getTime()) / oneDay);
  };

  const formatStopDate = (date: string, existingDate: string): string => {
    return date + "T" + existingDate.split("T")[1];
  };

  const formatFullDatetime = (date: Date | string) => {
    if (!date) return "";
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return format(dateObj, "EEE, MMM d, yyyy, h:mm aaa");
  };

  const formatFullDate = (date: Date | string) => {
    if (!date) return "";
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return format(dateObj, "EEE, MMM d, yyyy");
  };

  const today = getToday();

  /**
   * Calculates the time difference in minutes between two time strings in hh:mm format
   * @param newTime - The new time string in hh:mm format
   * @param oldTime - The old time string in hh:mm format
   * @returns The difference in minutes (positive if new time is later, negative if earlier)
   */
  const getTimeOffset = (newTime: string, oldTime: string): number => {
    if (!oldTime || !newTime) return 0;

    // Parse the time strings (format: "hh:mm")
    const [newHours, newMinutes] = newTime.split(":").map(Number);
    const [oldHours, oldMinutes] = oldTime.split(":").map(Number);

    // Create Date objects with the same date but different times
    const baseDate = new Date();
    baseDate.setHours(0, 0, 0, 0); // Reset to midnight

    const newDate = new Date(baseDate);
    newDate.setHours(newHours, newMinutes, 0, 0);

    const oldDate = new Date(baseDate);
    oldDate.setHours(oldHours, oldMinutes, 0, 0);

    // Calculate the time difference in milliseconds and convert to minutes
    const diffMinutes = (newDate.getTime() - oldDate.getTime()) / (1000 * 60);

    // Return 0 for no change, positive for later time, negative for earlier time
    return diffMinutes;
  };

  /**
   * Adjusts a date by a number of days
   * @param date - The date to adjust
   * @param dayOffset - The number of days to add (negative to subtract)
   * @returns A new Date object with the adjusted date, or undefined if input is undefined
   */
  const adjustDate = (date: Date | undefined, dayOffset: number): Date | undefined => {
    if (!date || dayOffset === 0) return date;
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + dayOffset);
    return newDate;
  };

  /**
   * Adjusts a time string in hh:mm format by a specified number of minutes
   * @param timeString - The time string in hh:mm format
   * @param minutesToAdd - The number of minutes to add (negative to subtract)
   * @returns The adjusted time string in hh:mm format
   */
  const adjustTime = (timeString: string | undefined, minutesToAdd: number): string | undefined => {
    if (!timeString || minutesToAdd === 0) return timeString;

    // Parse the hours and minutes
    const [hours, mins] = timeString.split(":").map(Number);

    // Calculate total minutes
    let totalMinutes = hours * 60 + mins + minutesToAdd;

    // Handle overflow/underflow
    while (totalMinutes < 0) totalMinutes += 24 * 60;
    totalMinutes = totalMinutes % (24 * 60);

    // Calculate new hours and minutes
    const newHours = Math.floor(totalMinutes / 60);
    const newMinutes = totalMinutes % 60;

    // Format back to hh:mm
    return `${newHours.toString().padStart(2, "0")}:${newMinutes.toString().padStart(2, "0")}`;
  };

  return {
    getDate,
    getTime,
    getDateTime,
    formatISODate,
    formatISODatetime,
    formatDate,
    formatTime,
    formatDatetime,
    formatDatetimeForFilename,
    getNewDate,
    getDayOffset,
    formatStopDate,
    formatFullDatetime,
    formatFullDate,
    today,
    getTimeOffset,
    adjustDate,
    adjustTime,
  };
}
